"use client";

import { useEffect } from "react";
import { captureException } from "@client/observability/capture";
import { BRAND_ESPRESSO, BRAND_SEMANTIC } from "@shared/brand";

/**
 * Next.js root error boundary. Catches errors thrown above `app/error.tsx`
 * (root layout, providers, the html/body shell). Must render its own `<html>`
 * and `<body>` because the root layout did not get to render.
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureException(error, {
      runtime: "web",
      component: "RootErrorBoundary",
      code: "GLOBAL_ERROR",
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          backgroundColor: BRAND_SEMANTIC.background,
          color: BRAND_SEMANTIC.textPrimary,
          fontFamily:
            "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
        }}
      >
        <div style={{ maxWidth: "28rem", textAlign: "center" }}>
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: "0.75rem",
            }}
          >
            Something went wrong
          </h1>
          <p style={{ color: BRAND_SEMANTIC.textSecondary, marginBottom: "2rem" }}>
            An unexpected error occurred. Please try again.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              backgroundColor: BRAND_ESPRESSO[600],
              color: BRAND_SEMANTIC.background,
              padding: "0.75rem 1.5rem",
              borderRadius: "9999px",
              border: "none",
              fontWeight: 500,
              fontSize: "1rem",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
